import React, { useEffect, useState } from 'react'
import Box from '@mui/joy/Box/index.js'
import Typography from '@mui/joy/Typography/index.js'
import Input from '@mui/joy/Input/index.js'
import List from '@mui/joy/List/index.js'
import ListItem from '@mui/joy/ListItem/index.js'
import ListItemButton from '@mui/joy/ListItemButton/index.js'
import ListItemContent from '@mui/joy/ListItemContent/index.js'
import Sheet from '@mui/joy/Sheet/index.js'
import Chip from '@mui/joy/Chip/index.js'
import SearchIcon from '@mui/icons-material/Search'
import PersonIcon from '@mui/icons-material/Person'
import CircularProgress from '@mui/joy/CircularProgress/index.js'
import Modal from '@mui/joy/Modal/index.js'
import ModalDialog from '@mui/joy/ModalDialog/index.js'
import Button from '@mui/joy/Button/index.js'
import { get } from 'aws-amplify/api'
import { fetchAuthSession } from 'aws-amplify/auth'
import dayjs from 'dayjs'

export default function UserSelector({ isOpen, onClose, onSelectUser }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchUsers = async (search = '') => {
    setLoading(true)
    setError(null)

    const auth = await fetchAuthSession()
    const authUsername = auth?.tokens?.idToken?.payload['cognito:username']

    const idToken = auth.tokens?.idToken?.toString()

    try {
      const queryParams = search ? { search } : undefined
      const restOperation = get({
        apiName: 'technician-bot',
        path: `/1/users`,
        options: {
          headers: {
            Authorization: idToken,
          },
          queryParams,
        },
      })

      const { body } = await restOperation.response
      const response = await body.json()

      const filteredUsers = response.filter(
        (user) => user.username !== authUsername,
      )

      setUsers(filteredUsers || [])
    } catch (err) {
      console.error('Error fetching users:', err)
      setError('Failed to load users. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      void fetchUsers()
    }
  }, [isOpen])

  const handleSearch = (event) => {
    const value = event.target.value
    if (value === 'all') {
      if (onSelectUser) {
        onSelectUser({ username: 'all' })
      }
      if (onClose) {
        onClose()
      }
    }
    setSearchTerm(value)

    // Debounce search requests
    const timeoutId = setTimeout(() => {
      void fetchUsers(value)
    }, 300)

    return () => {
      clearTimeout(timeoutId)
    }
  }

  const handleSelectUser = (user) => {
    if (onSelectUser) {
      onSelectUser(user)
    }
    if (onClose) {
      onClose()
    }
  }

  return (
    <Modal open={isOpen} onClose={onClose}>
      <ModalDialog
        size="lg"
        sx={{
          width: '600px',
          maxWidth: '90vw',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography level="title-lg">Select User</Typography>

        <Box sx={{ mt: 2, mb: 2 }}>
          <Input
            placeholder="Search by user ID, username, or email..."
            value={searchTerm}
            onChange={handleSearch}
            startDecorator={<SearchIcon />}
            fullWidth
          />
        </Box>

        {error && (
          <Typography level="body-sm" color="danger" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Sheet
          sx={{
            flex: 1,
            overflow: 'auto',
            borderRadius: 'sm',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {loading ?
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '300px',
              }}
            >
              <CircularProgress />
            </Box>
          : users.length === 0 ?
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '300px',
              }}
            >
              <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                {searchTerm ?
                  'No users found matching your search'
                : 'No users found'}
              </Typography>
            </Box>
          : <List>
              {users.map((user) => (
                <ListItem key={user.username}>
                  <ListItemButton
                    onClick={() => {
                      handleSelectUser(user)
                    }}
                    sx={{
                      p: 2,
                      borderRadius: 'sm',
                    }}
                  >
                    <ListItemContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 0.5,
                        }}
                      >
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <PersonIcon />
                          <Typography level="title-sm">
                            {user.fullName || user.username}
                          </Typography>
                        </Box>
                        <Typography
                          level="body-xs"
                          sx={{ color: 'text.tertiary' }}
                        >
                          Created: {dayjs(user.createdAt).format('MMM D, YYYY')}
                        </Typography>
                      </Box>
                      {user.email && (
                        <Typography
                          level="body-sm"
                          sx={{ color: 'text.secondary' }}
                        >
                          {user.email}
                        </Typography>
                      )}
                      <Chip
                        size="sm"
                        variant="soft"
                        color="primary"
                        sx={{ mt: 1 }}
                      >
                        {user.fullName}
                      </Chip>
                    </ListItemContent>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          }
        </Sheet>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose} variant="outlined" color="neutral">
            Cancel
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  )
}
